'use client';

import { useState, useEffect } from 'react';
import { useGetProviderListMembersMutation } from 'src/api/redux/lists/listsApi';

/**
 * Hook to fetch all members from multiple provider lists with pagination
 * Uses RTK Query mutation to recursively fetch all pages for each list
 *
 * @param prospectListIds - Array of list IDs to fetch members from
 * @returns Object with data (array of all members), isLoading, and error
 */
const useGetRepProspectLists = (prospectListIds: string[]) => {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const [getMembers] = useGetProviderListMembersMutation();

  useEffect(() => {
    // Reset when list IDs change
    setData([]);
    setError(null);

    if (!prospectListIds || prospectListIds.length === 0) {
      setIsLoading(false);
      return;
    }

    const fetchAllMembers = async () => {
      setIsLoading(true);
      try {
        const allResults: any[] = [];

        // Fetch all members from all lists in parallel
        await Promise.all(
          prospectListIds.map(async (listId) => {
            let queryParams = '';
            let hasMore = true;

            // Paginate through all pages for this list
            while (hasMore) {
              const result = await getMembers({
                listId,
                queryParams,
                data: {},
              }).unwrap();

              if (result?.results) {
                allResults.push(...result.results);
              }

              // Check if there's a next page
              if (result?.next) {
                // Extract query params from next URL
                const nextUrl = new URL(result.next);
                queryParams = nextUrl.search;
              } else {
                hasMore = false;
              }
            }
          })
        );

        setData(allResults);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to fetch list members:', err);
        setError(err);
        setIsLoading(false);
      }
    };

    fetchAllMembers();
  }, [prospectListIds?.join(','), getMembers]);

  return {
    data: data.length > 0 ? data : undefined,
    isLoading,
    error,
  };
};

export default useGetRepProspectLists;
